import * as LucideIcons from '@lucide/vue';
import { defineAsyncComponent, type Component } from 'vue';

import { getTabComponent, hasTabComponent } from '../services/componentRegistry';
import type { BlockConfig, FieldConfig, TabConfig } from '../types';

/**
 * Shared helpers for rendering viewer tabs in both the full-page CrudViewer and
 * the slide-out ViewerSheet. Centralizes the lookup/cache for custom tab
 * components, the icon resolver, and the synthetic-block fallback used when a
 * tab declares fields directly (via `TabBuilder::field()`).
 *
 * Each call returns a fresh module-private cache so concurrently mounted
 * viewers don't collide on async-component resolution.
 */
export function useViewerTabs() {
    const asyncTabCache = new Map<string, Component>();

    function getViewerTabComponent(componentName: string): Component | null {
        if (!componentName) return null;
        if (asyncTabCache.has(componentName)) return asyncTabCache.get(componentName)!;
        const entry = getTabComponent(componentName);
        if (!entry) return null;
        if (typeof entry === 'function') {
            const asyncComp = defineAsyncComponent(entry as () => Promise<{ default: Component }>);
            asyncTabCache.set(componentName, asyncComp);
            return asyncComp;
        }
        const resolved = (entry as { default: Component }).default ?? null;
        if (resolved) asyncTabCache.set(componentName, resolved);
        return resolved;
    }

    function isCustomTab(tab: TabConfig): boolean {
        return !!tab.component && hasTabComponent(tab.component);
    }

    function getIcon(iconName: string | null | undefined): Component | null {
        if (!iconName) return null;
        return ((LucideIcons as Record<string, unknown>)[iconName] as Component | undefined) ?? null;
    }

    /**
     * Materialize the renderable blocks for a tab.
     *
     * Tabs may declare fields directly on the tab (mirroring
     * `EditorBuilder` / `TabBuilder::field()`), in which case those fields
     * are wrapped in a synthetic, headerless block so the standard block
     * renderer can lay them out — otherwise tab-level fields would silently
     * disappear from the viewer.
     */
    function blocksForTab(tab: TabConfig): BlockConfig[] {
        const explicit = (tab.blocks ?? []) as BlockConfig[];
        const directFields = (tab.fields ?? []) as FieldConfig[];
        if (directFields.length === 0) return explicit;
        const implicit: BlockConfig = {
            key: `${tab.key}__implicit_fields`,
            description: null,
            icon: null,
            fields: directFields,
            isBreakpoint: false,
            permission: null,
            component: null,
            componentProps: null,
        };
        return [implicit, ...explicit];
    }

    return {
        getViewerTabComponent,
        isCustomTab,
        getIcon,
        blocksForTab,
    };
}
